import {
    type CookieConsents_ResetFunction,
    type CookieConsents_AddChangeListener,
    type CookieConsents_GetFunction,
    type CookieConsents_RemoveChangeListener,
    type CookieConsents_SetAllFunction,
    type CookieConsents_SetFunction,
} from "../cookie-consent/models";
import {
    type ConsentObject,
    type UpdateCallback,
    type ConsentKey,
    type ConsentValue,
} from "./types";

const bypass = window.CookieConsent_Bypass === true;
let storedInitially = false;

const updateCallbacks: UpdateCallback[] = [];
const keys: ConsentKey[] = [
    "necessary",
    "functional",
    "experienceEnhancement",
    "statistics",
    "marketing",
];

const hasCookieStringFormatError = function (value: string) {
    try {
        JSON.parse(decodeURIComponent(value.trim()));
        return false;
    } catch (e) {
        //console.warn("Cookie value error: ", e);
        return true;
    }
};

const getCookieConsentObj = function (): ConsentObject<ConsentValue> {
    if (bypass === true) {
        return keys.reduce(function (obj, key) {
            obj[key] = true;
            return obj;
        }, {} as ConsentObject);
    }
    let value = document.cookie.split(";").find(function (c) {
        return c.trim().indexOf("_odendo_cookieConsent_value=") === 0;
    });

    if (
        (value == null && !storedInitially) ||
        (value != null &&
            hasCookieStringFormatError(
                value.trim().substring("_odendo_cookieConsent_value=".length)
            ))
    ) {
        storedInitially = true;
        value = storeCookieConsentObj(
            keys.reduce(function (obj, key) {
                obj[key] = null;
                return obj;
            }, {} as ConsentObject),
            true
        );
    } else {
        if (value == null) {
            //throw new Error("Cookie value error");
            return keys.reduce(function (obj, key) {
                obj[key] = null;
                return obj;
            }, {} as ConsentObject);
        }
        value = value.trim().substring("_odendo_cookieConsent_value=".length);
    }

    const cookieConsents = JSON.parse(
        decodeURIComponent(value?.trim() ?? "")
    ) as ConsentObject;
    return cookieConsents;
};

// DONT TRY TO CHANGE THIS FUNCTION INTO REST PARAMETERS
// IT WILL BREAK AND NOT UPDATE DATALAYER PROPERLY
// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
function gtag(a?: any, b?: any, c?: any, e?: any, f?: any, g?: any) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as DataLayerItem);
    console.log(window.dataLayer);
}

type TagManagerKey =
    | "ad_storage"
    | "ad_user_data"
    | "ad_personalization"
    | "analytics_storage";

const tagManagerRelations: Record<TagManagerKey, ConsentKey[]> = {
    ad_storage: ["statistics", "marketing", "experienceEnhancement"],
    ad_user_data: ["statistics", "marketing", "experienceEnhancement"],
    ad_personalization: ["marketing", "experienceEnhancement"],
    analytics_storage: ["statistics", "marketing", "experienceEnhancement"],
};

function createTagManagerConsentState(obj: ConsentObject<ConsentValue>) {
    const state: TagManagerConsentState = {
        ad_personalization: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        analytics_storage: "denied",
    };
    for (const key of Object.keys(tagManagerRelations)) {
        const relations = tagManagerRelations[key as TagManagerKey];
        state[key as TagManagerKey] = relations.every(
            (consentKey) => obj[consentKey] === true
        )
            ? "granted"
            : "denied";
    }
    return state;
}

function tagManagerStateDiff(
    oldState: TagManagerConsentState,
    newState: TagManagerConsentState
) {
    const diff: Partial<TagManagerConsentState> = {};
    for (const key of Object.keys(tagManagerRelations)) {
        if (oldState[key as TagManagerKey] !== newState[key as TagManagerKey]) {
            diff[key as TagManagerKey] = newState[key as TagManagerKey];
        }
    }
    return diff;
}

type TagManagerConsentState = Record<TagManagerKey, "denied" | "granted">;

function defaultConsentObject(): ConsentObject<ConsentValue> {
    return {
        experienceEnhancement: false,
        functional: true,
        marketing: false,
        necessary: true,
        statistics: false,
    };
}

function updateConsentState(
    obj: ConsentObject<ConsentValue>,
    oldObj: ConsentObject<ConsentValue> | undefined = undefined
) {
    const oldState = createTagManagerConsentState(
        oldObj ?? defaultConsentObject()
    );
    const newState = createTagManagerConsentState(obj);

    const diffState = tagManagerStateDiff(oldState, newState);
    if (Object.keys(diffState).length > 0) {
        console.log("update tag manager");
        console.log(diffState);
        gtag("consent", "update", diffState);
    } else {
        console.log("no data to update in tag manager");
    }

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const newValue = obj[key];

        if ((oldObj == null || oldObj[key] !== true) && newValue === true) {
            fireCustomEvent(key);
        }
    }
}

function fireCustomEvent(key: ConsentKey) {
    if (window.dataLayer == null) {
        const er = new Error("No datalayer");
        setTimeout(() => {
            throw er;
        }, 0);
        console.error(er);
        return;
    }
    const eventName = "cookie_category_" + key + "_accepted";
    window.dataLayer.push({ event: eventName });
    //console.log("event: "+eventName);
}

let updateCallbacksRunning = false;

function runUpdateCallbacks(obj: ConsentObject) {
    if (updateCallbacksRunning) return;
    updateCallbacksRunning = true;
    try {
        updateCallbacks.forEach(function (fnc) {
            try {
                fnc(obj);
            } catch (e) {
                const er = e;
                console.error(e);
                setTimeout(() => {
                    throw er;
                }, 0);
            }
        });
    } finally {
        updateCallbacksRunning = false;
    }
}

const storeCookieConsentObj = function (
    obj: ConsentObject,
    initialStoring?: boolean | undefined | null
) {
    if (bypass) {
        obj = keys.reduce(function (obj, key) {
            obj[key] = true;
            return obj;
        }, {} as ConsentObject);
    }
    let oldObj = null;
    const value = encodeURIComponent(JSON.stringify(obj));
    if (initialStoring !== true) {
        oldObj = getCookieConsentObj();
    } else {
        oldObj = keys.reduce(function (obj, key) {
            obj[key] = null;
            return obj;
        }, {} as ConsentObject);
    }
    if (bypass !== true) {
        console.log("add cookie consent value: " + value);
        const secureDirective = location.protocol === "http:" ? "" : "; Secure";
        const sameSiteDirectory =
            location.hostname === "localhost" ? "" : ";SameSite=None";
        document.cookie =
            "_odendo_cookieConsent_value=" +
            value +
            ";max-age=31536000" +
            sameSiteDirectory +
            secureDirective;
    }
    runUpdateCallbacks(obj);
    console.log("COMPARE", oldObj);
    if (oldObj != null) {
        updateConsentState(obj, oldObj);
    }
    return value;
};

const controller = {
    get(key: ConsentKey) {
        const cookieConsents = getCookieConsentObj();
        return cookieConsents[key];
    },
    set(key: ConsentKey, value: ConsentObject[ConsentKey]) {
        const cookieConsents = getCookieConsentObj();
        cookieConsents[key] = value;
        storeCookieConsentObj(cookieConsents);
    },
    setAll(obj: ConsentObject) {
        if (obj == null) throw new Error("cannot set all from null/undefined");
        const setObj = {} as ConsentObject;
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (!obj.hasOwnProperty(key))
                throw new Error("object is missing " + key);
            const val = obj[key];
            if (
                val !== null &&
                val !== undefined &&
                val !== true &&
                val !== false
            ) {
                throw new Error("invalid value for " + key + ": " + val);
            }
            setObj[key] = val;
        }
        storeCookieConsentObj(setObj);
    },
    addChangeListener(fnc: UpdateCallback) {
        updateCallbacks.push(fnc);
    },
    removeChangeListener(fnc: UpdateCallback) {
        const index = updateCallbacks.indexOf(fnc);
        if (index >= 0) {
            updateCallbacks.splice(index, 1);
        }
    },
};

const getObjKey = function (i: string | number): ConsentKey {
    if (typeof i === "string" && !isNaN(parseInt(i))) {
        i = parseInt(i);
    }
    if (typeof i === "number") {
        i = keys[i];
    }
    if (typeof i !== "string") throw new Error("Unknown parameter: " + i);

    return i as ConsentKey;
};

Object.defineProperty(window, "cookieConsents", {
    get: function () {
        return getCookieConsentObj();
    },
});

export const cookieConsents_set: CookieConsents_SetFunction = function (
    i: string | number | ConsentKey,
    value: ConsentObject[ConsentKey]
) {
    const key = getObjKey(i);

    controller.set(key, value);
    console.log("stored " + key + ": " + value);
};

export const cookieConsents_setAll: CookieConsents_SetAllFunction = function (
    obj: ConsentObject
) {
    controller.setAll(obj);
    console.log("stored obj", obj);
};

export const cookieConsents_reset: CookieConsents_ResetFunction = function () {
    cookieConsents_setAll(
        keys.reduce(function (obj, key) {
            obj[key] = null;
            return obj;
        }, {} as ConsentObject)
    );
    window.location.reload();
};

export const cookieConsents_addChangeListener: CookieConsents_AddChangeListener =
    function (fnc: UpdateCallback) {
        controller.addChangeListener(fnc);
    };

export const cookieConsents_removeChangeListener: CookieConsents_RemoveChangeListener =
    function (fnc: UpdateCallback) {
        controller.removeChangeListener(fnc);
    };

export const cookieConsents_get: CookieConsents_GetFunction = function (
    i,
    defaultValue
) {
    let value = null;
    const key = getObjKey(i);
    value = controller.get(key);

    if (value === true || value === false) return value;
    return defaultValue;
};

if (!("initialOdendoPostpone" in window)) {
    window["initialOdendoPostpone"] = [];
}

// fire initial events for already loaded values
{
    const cookieConsents = window.cookieConsents;
    updateConsentState(cookieConsents);
}

type DataLayerArrayData = [
    "consent",
    "update" | "default",
    Partial<TagManagerConsentState>
];

type DataLayerItem = { event?: string } | DataLayerArrayData;

declare global {
    interface Window {
        CookieConsent_Bypass?: boolean | undefined | null;
        dataLayer?: DataLayerItem[];
        initialOdendoPostpone?: Promise<any>[];

        cookieConsents: ConsentObject;
        cookieConsents_set: CookieConsents_SetFunction;
        cookieConsents_reset: CookieConsents_ResetFunction;
        cookieConsents_addChangeListener: CookieConsents_AddChangeListener;
        cookieConsents_removeChangeListener: CookieConsents_RemoveChangeListener;
        cookieConsents_setAll: CookieConsents_SetAllFunction;
        cookieConsents_get: CookieConsents_GetFunction;
    }
}
